import {
  FileUploadActionTypes,
  IFileUploadSuccessAction,
} from '@app/components/FileUpload/modules/fileUpload'
import { Address } from '@app/models/Address'
import { AssociatedContact } from '@app/models/AssociatedContact'
import { AssociatedGroup } from '@app/models/AssociatedGroup'
import { Attachment } from '@app/models/Attachment'
import { Company } from '@app/models/Company'
import { CompanyContact } from '@app/models/CompanyContact'
import { Contact, IContact } from '@app/models/Contact'
import { EmailAddress } from '@app/models/EmailAddress'
import { Group, IGroup } from '@app/models/Group'
import { PhoneNumber } from '@app/models/PhoneNumber'
import { ISocialConnection } from '@app/models/SocialConnection'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { getCompanyContactsList } from '@app/store/modules/global'

// -------------------------
// STATE
// -------------------------

export interface IConnectionEditState {
  readonly canSave: boolean
  readonly connection: IContact
  readonly selectedConnections: CompanyContact[]
}

const StateRecord = Record({
  canSave: false,
  connection: undefined,
  selectedConnections: [],
})

export class ConnectionEditState extends StateRecord
  implements IConnectionEditState {
  public static fromJS(initialState?: Partial<IConnectionEditState>) {
    return new ConnectionEditState(initialState)
  }

  public readonly canSave: boolean
  public readonly connection: Contact
  public readonly selectedConnections: CompanyContact[]

  constructor(props?: Partial<IConnectionEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  CreateGroup = 'CONNECTION_EDIT::CREATE_GROUP',
  CreateGroupSuccess = 'CONNECTION_EDIT::CREATE_GROUP_SUCCESS',
  LoadConnection = 'CONNECTION_EDIT::LOAD_CONNECTION',
  LoadConnectionSuccess = 'CONNECTION_EDIT::LOAD_CONNECTION_SUCCESS',
  ResetState = 'CONNECTION_EDIT::RESET_STATE',
  SaveConnection = 'CONNECTION_EDIT::SAVE_CONNECTION',
  SaveConnectionFailure = 'CONNECTION_EDIT::SAVE_CONNECTION_FAILURE',
  SaveConnectionSuccess = 'CONNECTION_EDIT::SAVE_CONNECTION_SUCCESS',
  SetAddresses = 'CONNECTION_EDIT::SET_ADDRESSES',
  SetCompany = 'CONNECTION_EDIT::SET_COMPANY',
  SetBirthday = 'CONNECTION_EDIT::SET_BIRTHDAY',
  SetEmailAddresses = 'CONNECTION_EDIT::SET_EMAIL_ADDRESSES',
  SetFirstName = 'CONNECTION_EDIT::SET_FIRST_NAME',
  SetIsCompany = 'CONNECTION_EDIT::SET_IS_COMPANY',
  SetLastName = 'CONNECTION_EDIT::SET_LAST_NAME',
  SetPrivateNote = 'CONNECTION_EDIT::SET_PRIVATE_NOTE',
  SetProfileImage = 'CONNECTION_EDIT::SET_PROFILE_IMAGE',
  SetPublicNote = 'CONNECTION_EDIT::SET_PUBLIC_NOTE',
  SetPhoneNumbers = 'CONNECTION_EDIT::SET_PHONE_NUMBERS',
  SetSocial = 'CONNECTION_EDIT::SET_SOCIAL',
  SetTitle = 'CONNECTION_EDIT::SET_TITLE',
  SetWebsite = 'CONNECTION_EDIT::SET_WEBSITE',
  GetConnections = 'CONNECTION_EDIT::GET_CONNECTIONS',
  SetConnections = 'CONNECTION_EDIT::SET_CONNECTIONS',
  SetSelectedGroups = 'CONNECTION_EDIT::SET_SELECTED_GROUPS',
}

interface ICreateGroupAction {
  type: ActionTypes.CreateGroup
  value: string
}

interface ICreateGroupSuccessAction {
  type: ActionTypes.CreateGroupSuccess
  value: IGroup
}

interface ILoadConnectionAction {
  type: ActionTypes.LoadConnection
  value?: string
}

interface ILoadConnectionSuccessAction {
  type: ActionTypes.LoadConnectionSuccess
  value: Contact
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveConnectionAction {
  type: ActionTypes.SaveConnection
  value?: string
}

interface ISaveConnectionFailureAction {
  type: ActionTypes.SaveConnectionFailure
  error: string
}

interface ISaveConnectionSuccessAction {
  type: ActionTypes.SaveConnectionSuccess
}

interface ISetAddressesAction {
  type: ActionTypes.SetAddresses
  value: Address[]
}

interface ISetBirthdayAction {
  type: ActionTypes.SetBirthday
  value?: number
}

interface ISetCompanyAction {
  type: ActionTypes.SetCompany
  value?: Company
}

interface ISetEmailAddressesAction {
  type: ActionTypes.SetEmailAddresses
  value: List<EmailAddress>
}

interface ISetFirstNameAction {
  type: ActionTypes.SetFirstName
  value: string
}

interface ISetIsCompanyAction {
  type: ActionTypes.SetIsCompany
  value: boolean
}

interface ISetLastNameAction {
  type: ActionTypes.SetLastName
  value: string
}

interface ISetPhoneNumbersAction {
  type: ActionTypes.SetPhoneNumbers
  value: List<PhoneNumber>
}

interface ISetPrivateNoteAction {
  type: ActionTypes.SetPrivateNote
  value: string
}

interface ISetProfileImageAction {
  type: ActionTypes.SetProfileImage
  attachment: Attachment
}

interface ISetPublicNoteAction {
  type: ActionTypes.SetPublicNote
  value: string
}

interface ISetSocialAction {
  type: ActionTypes.SetSocial
  value: ISocialConnection
}

interface ISetSelectedGroupAction {
  type: ActionTypes.SetSelectedGroups
  value: Group[]
}

interface ISetSelectedConnectionsAction {
  type: ActionTypes.SetConnections
  value: CompanyContact[]
}

interface ISetTitleAction {
  type: ActionTypes.SetTitle
  value: string
}

interface ISetWebsiteAction {
  type: ActionTypes.SetWebsite
  value: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICreateGroupSuccessAction
  | ILoadConnectionAction
  | ILoadConnectionSuccessAction
  | IResetStateAction
  | ISaveConnectionAction
  | ISaveConnectionFailureAction
  | ISaveConnectionSuccessAction
  | ISetAddressesAction
  | ISetBirthdayAction
  | ISetCompanyAction
  | ISetEmailAddressesAction
  | ISetFirstNameAction
  | ISetIsCompanyAction
  | ISetLastNameAction
  | ISetPhoneNumbersAction
  | ISetPrivateNoteAction
  | ISetProfileImageAction
  | ISetPublicNoteAction
  | ISetSocialAction
  | ISetSelectedGroupAction
  | ISetSelectedConnectionsAction
  | ISetTitleAction
  | ISetWebsiteAction

// -------------------------
// ACTION CREATORS
// -------------------------
function createGroup(value: string): ICreateGroupAction {
  return { type: ActionTypes.CreateGroup, value }
}

function createGroupSuccess(value: IGroup): ICreateGroupSuccessAction {
  return { type: ActionTypes.CreateGroupSuccess, value }
}

function loadConnection(value?: string): ILoadConnectionAction {
  return { type: ActionTypes.LoadConnection, value }
}

function loadConnectionSuccess(value: Contact): ILoadConnectionSuccessAction {
  return {
    type: ActionTypes.LoadConnectionSuccess,
    value,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function save(value?: string): ISaveConnectionAction {
  return {
    type: ActionTypes.SaveConnection,
    value,
  }
}

function saveFailure(error: string): ISaveConnectionFailureAction {
  return {
    error,
    type: ActionTypes.SaveConnectionFailure,
  }
}

function saveSuccess(): ISaveConnectionSuccessAction {
  return {
    type: ActionTypes.SaveConnectionSuccess,
  }
}

function setAddresses(value: Address[]): ISetAddressesAction {
  return {
    type: ActionTypes.SetAddresses,
    value,
  }
}

function setBirthday(value?: number): ISetBirthdayAction {
  return {
    type: ActionTypes.SetBirthday,
    value,
  }
}

function setCompany(value?: Company): ISetCompanyAction {
  return {
    type: ActionTypes.SetCompany,
    value,
  }
}

function setEmailAddresses(
  value: List<EmailAddress>,
): ISetEmailAddressesAction {
  return {
    type: ActionTypes.SetEmailAddresses,
    value,
  }
}

function setFirstName(value: string): ISetFirstNameAction {
  return {
    type: ActionTypes.SetFirstName,
    value,
  }
}

function setIsCompany(value: boolean): ISetIsCompanyAction {
  return {
    type: ActionTypes.SetIsCompany,
    value,
  }
}

function setLastName(value: string): ISetLastNameAction {
  return {
    type: ActionTypes.SetLastName,
    value,
  }
}

function setPhoneNumbers(value: List<PhoneNumber>): ISetPhoneNumbersAction {
  return {
    type: ActionTypes.SetPhoneNumbers,
    value,
  }
}

function setPrivateNote(value: string): ISetPrivateNoteAction {
  return {
    type: ActionTypes.SetPrivateNote,
    value,
  }
}

function setProfileImage(attachment: Attachment): ISetProfileImageAction {
  return {
    attachment,
    type: ActionTypes.SetProfileImage,
  }
}

function setNote(value: string): ISetPublicNoteAction {
  return {
    type: ActionTypes.SetPublicNote,
    value,
  }
}

function setSocial(value: ISocialConnection): ISetSocialAction {
  return {
    type: ActionTypes.SetSocial,
    value,
  }
}

function setSelectedGroups(value: Group[]): ISetSelectedGroupAction {
  return { type: ActionTypes.SetSelectedGroups, value }
}

function setSelectedConnections(
  value: CompanyContact[],
): ISetSelectedConnectionsAction {
  return { type: ActionTypes.SetConnections, value }
}

function setTitle(value: string): ISetTitleAction {
  return { type: ActionTypes.SetTitle, value }
}

function setWebsite(value: string): ISetWebsiteAction {
  return {
    type: ActionTypes.SetWebsite,
    value,
  }
}

export const actionCreators = {
  createGroup,
  createGroupSuccess,
  loadConnection,
  loadConnectionSuccess,
  resetState,
  save,
  setAddresses,
  setBirthday,
  setCompany,
  setEmailAddresses,
  setFirstName,
  setIsCompany,
  setLastName,
  setNote,
  setPhoneNumbers,
  setPrivateNote,
  setSelectedConnections,
  setSelectedGroups,
  setSocial,
  setTitle,
  setWebsite,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ConnectionEditState> = (
  state: ConnectionEditState = new ConnectionEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.CreateGroupSuccess:
      return state.setIn(
        ['connection', 'groups', state.connection.groups.size],
        new AssociatedGroup({
          groupId: action.value.id,
          name: action.value.name,
        }),
      ) as ConnectionEditState

    case ActionTypes.LoadConnection:
      if (!action.value) {
        const newContact = new Contact()
        return state.set('connection', newContact) as ConnectionEditState
      } else {
        return state as ConnectionEditState
      }

    case ActionTypes.LoadConnectionSuccess:
      return state
        .set('connection', action.value)
        .set('selectedConnections', action.value.associated.toArray())
        .set('canSave', true) as ConnectionEditState

    case ActionTypes.ResetState:
      return state
        .set('canSave', false)
        .set('connection', undefined)
        .set('selectedConnections', []) as ConnectionEditState

    case ActionTypes.SaveConnection:
      return state as ConnectionEditState

    case ActionTypes.SaveConnectionFailure:
      return state as ConnectionEditState

    case ActionTypes.SaveConnectionSuccess:
      return state as ConnectionEditState

    case ActionTypes.SetAddresses:
      return state.setIn(
        ['connection', 'addresses'],
        action.value,
      ) as ConnectionEditState

    case ActionTypes.SetBirthday:
      return state.setIn(
        ['connection', 'birthday'],
        action.value,
      ) as ConnectionEditState

    case ActionTypes.SetCompany:
      return state.setIn(
        ['connection', 'company'],
        AssociatedContact.fromJS(action.value),
      ) as ConnectionEditState

    case ActionTypes.SetEmailAddresses:
      return state.setIn(
        ['connection', 'emailAddresses'],
        action.value,
      ) as ConnectionEditState

    case ActionTypes.SetFirstName:
      return state
        .setIn(['connection', 'firstName'], action.value)
        .set(
          'canSave',
          !state.connection.isCompany &&
            action.value.length > 0 &&
            state.connection.lastName.length > 0,
        ) as ConnectionEditState

    case ActionTypes.SetIsCompany:
      return state
        .setIn(['connection', 'isCompany'], action.value)
        .setIn(['connection', 'lastName'], '') as ConnectionEditState

    case ActionTypes.SetLastName:
      return state
        .setIn(['connection', 'lastName'], action.value)
        .set(
          'canSave',
          state.connection.isCompany
            ? action.value.length > 0
            : action.value.length > 0 && state.connection.firstName.length > 0,
        ) as ConnectionEditState

    case ActionTypes.SetPhoneNumbers:
      return state.setIn(
        ['connection', 'phoneNumbers'],
        action.value,
      ) as ConnectionEditState

    case ActionTypes.SetPrivateNote:
      return state.setIn(['connection', 'privateNote'], {
        note: action.value,
      }) as ConnectionEditState

    case ActionTypes.SetPublicNote:
      return state.setIn(
        ['connection', 'note'],
        action.value,
      ) as ConnectionEditState

    case ActionTypes.SetProfileImage:
      return state.setIn(
        ['connection', 'profileImage'],
        action.attachment,
      ) as ConnectionEditState

    case ActionTypes.SetSelectedGroups:
      return state.setIn(
        ['connection', 'groups'],
        List(action.value.map((item: any) => AssociatedGroup.fromJS(item))),
      ) as ConnectionEditState

    case ActionTypes.SetConnections:
      return (state
        .set('selectedConnections', action.value)
        .setIn(
          ['connection', 'associated'],
          action.value,
        ) as ConnectionEditState) as ConnectionEditState

    case ActionTypes.SetSocial:
      const social = state.getIn(['connection', 'social']).find((item: any) => {
        return item.type === action.value.type
      })

      if (!social) {
        return state.setIn(
          ['connection', 'social'],
          state.getIn(['connection', 'social']).push(action.value),
        ) as ConnectionEditState
      } else {
        const indexToUpdate = state
          .getIn(['connection', 'social'])
          .findIndex((item: any) => {
            return item.get('type') === action.value.type
          })

        return state.setIn(
          ['connection', 'social', indexToUpdate, 'username'],
          action.value.username,
        ) as ConnectionEditState
      }

    case ActionTypes.SetTitle:
      return state.setIn(
        ['connection', 'title'],
        action.value,
      ) as ConnectionEditState

    case ActionTypes.SetWebsite:
      return state.setIn(
        ['connection', 'url'],
        action.value,
      ) as ConnectionEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* createGroupSaga(action: ICreateGroupAction) {
  try {
    const baseUrl = '/api/Groups'

    const opts = {
      body: JSON.stringify({ name: action.value }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, opts)

    if (result.success === true) {
      yield put(successToast('Success', `Group ${result.entity.name} created`))
      yield put(createGroupSuccess(Group.fromJS(result.entity)))
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* getProfileImageSaga(action: IFileUploadSuccessAction) {
  try {
    const connection = yield select(selectConnection())

    if (connection) {
      yield put(setProfileImage(action.attachment))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadConnectionSaga(action: ILoadConnectionAction) {
  try {
    if (action.value) {
      const baseUrl = `/api/Contacts/${action.value}`

      const result = yield makeAuthenticatedRequest(baseUrl)

      if (result.success === true) {
        yield put(resetState())
        yield put(loadConnectionSuccess(Contact.fromJS(result.entity)))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* saveConnectionSaga(action: ISaveConnectionAction) {
  try {
    const state = yield select(selectModuleState())

    const baseUrl = action.value
      ? `/api/Contacts/${action.value}`
      : '/api/Contacts'

    const connection = state.connection.toJS()

    if (connection.user) {
      const permissions: { [key: string]: boolean } = {}
      for (const p of Object.keys(
        connection.user.permissions.specialPermissions,
      )) {
        permissions[p] = connection.user.permissions.specialPermissions[p]
      }
      connection.user.permissions = permissions
    }

    const connectionOpts = {
      body: JSON.stringify(connection),
      method: action.value ? RequestMethod.PUT : RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, connectionOpts)

    if (result.success === true) {
      yield put(saveSuccess())
      yield put(resetState())
      yield put(
        successToast(
          'Success',
          action.value
            ? `${state.connection.fullName}'s information updated`
            : 'New Connection Created',
        ),
      )
      yield put(push(`/connection/${action.value || result.entity.id}`))

      // Force update of company contacts list in global state
      yield put(getCompanyContactsList(true))
    } else {
      yield put(saveFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchCreateGroup() {
  yield takeLatest(ActionTypes.CreateGroup, createGroupSaga)
}

function* watchFileUpload() {
  yield takeLatest(FileUploadActionTypes.FileUploadSuccess, getProfileImageSaga)
}

function* watchLoadConnection() {
  yield takeLatest(ActionTypes.LoadConnection, loadConnectionSaga)
}

function* watchSaveConnection() {
  yield takeLatest(ActionTypes.SaveConnection, saveConnectionSaga)
}

export function* connectionEditRootSaga() {
  yield fork(watchCreateGroup)
  yield fork(watchLoadConnection)
  yield fork(watchSaveConnection)
  yield fork(watchFileUpload)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): ConnectionEditState => state.connectionEdit

export const selectCanSave = () =>
  createSelector(selectModuleState(), (mState) => mState.canSave)

export const selectConnection = () =>
  createSelector(selectModuleState(), (mState) => mState.connection)

export const selectSelectedConnections = () =>
  createSelector(selectModuleState(), (mState) => mState.selectedConnections)
